import { useAuthStore } from '@/stores/auth/auth-store';
import { ProjectRole, type Component, type Project, type User } from '@/types';
import type { CanvasAction } from '@/services/api/agentApi';
import { ComponentPermissionType } from '@/types/permissions';
import { hasAuthoritativePlatformAdminEntitlement } from '@/utils';
import { getViewerPermissionIdStrings } from '@/components/permissions/viewerPermissionIds';

export type AICommandMutationCategory =
  | 'content'
  | 'style'
  | 'layout'
  | 'insertion'
  | 'delete'
  | 'visibility'
  | 'canvas';

export interface AICommandPermissionRequest {
  categories: AICommandMutationCategory[];
  project?: Project | null;
  targetComponents?: Array<AICommandPermissionTarget | null | undefined>;
  targetComponentIds?: string[];
  isCanvasMutation?: boolean;
}

export interface AICommandPermissionResult {
  allowed: boolean;
  message?: string;
  blockedCategory?: AICommandMutationCategory;
}

export type AICommandPermissionGuard = (
  request: AICommandPermissionRequest
) => AICommandPermissionResult;

export interface AICommandPermissionGuardOptions {
  resolveComponentById?: (
    id: string
  ) => AICommandPermissionTarget | null | undefined;
}

type AICommandPermissionTarget = Pick<Component, 'id' | 'name' | 'type' | 'metadata'> & {
  permissions?: unknown;
};

const BLOCKED_MESSAGES: Record<AICommandMutationCategory, string> = {
  content: "Blocked by permissions: content edits are not allowed here.",
  style: "Blocked by permissions: style edits are not allowed here.",
  layout: "Blocked by permissions: layout, position, or resize edits are not allowed here.",
  insertion: "Blocked by permissions: component insertion is not allowed here.",
  delete: "Blocked by permissions: component deletion is not allowed here.",
  visibility: "Blocked by permissions: visibility edits are not allowed here.",
  canvas: "Blocked by permissions: canvas edits are not allowed here.",
};

const CATEGORY_LABELS: Record<AICommandMutationCategory, string> = {
  content: 'content edits',
  style: 'style edits',
  layout: 'layout, position, or resize edits',
  insertion: 'component insertion',
  delete: 'component deletion',
  visibility: 'visibility edits',
  canvas: 'canvas edits',
};

const STYLE_KEYS = new Set([
  'color',
  'backgroundColor',
  'borderColor',
  'borderStyle',
  'borderWidth',
  'opacity',
  'display',
  'border',
  'borderRadius',
  'boxShadow',
  'textShadow',
  'padding',
  'margin',
  'position',
  'top',
  'left',
  'right',
  'bottom',
  'zIndex',
  'transform',
  'cursor',
  'overflow',
  'gap',
  'rowGap',
  'columnGap',
  'flexDirection',
  'flexWrap',
  'justifyContent',
  'alignItems',
  'alignContent',
  'justifyItems',
  'placeContent',
  'placeItems',
]);

const VISUAL_PROPERTY_STYLE_KEYS = new Set([
  'fontSize',
  'fontWeight',
  'fontFamily',
  'fontStyle',
  'lineHeight',
  'letterSpacing',
  'textAlign',
  'textTransform',
  'textDecoration',
  'textDecorationLine',
  'textDecorationStyle',
  'textDecorationColor',
  'textDecorationThickness',
  'icon',
  'iconName',
  'iconSize',
  'iconColor',
  'iconPosition',
  'shape',
  'rotation',
  'stroke',
  'strokeWidth',
  'fill',
  'fillColor',
]);

const LAYOUT_KEYS = new Set([
  'parentId',
  'children',
  'order',
  'zIndex',
  'size',
  'width',
  'height',
  'position',
  'responsivePosition',
  'responsiveStyles',
  'responsive',
  'layout',
  'breakpoint',
  'top',
  'left',
  'right',
  'bottom',
  'gap',
  'rowGap',
  'columnGap',
  'flexDirection',
  'flexWrap',
  'justifyContent',
  'alignItems',
  'alignContent',
  'justifyItems',
  'placeContent',
  'placeItems',
]);

const CONTENT_KEYS = new Set([
  'properties',
  'content',
  'text',
  'label',
  'title',
  'placeholder',
  'value',
  'data',
  'columns',
  'queryBinding',
  'chartType',
  'type',
]);

const PERMISSION_MANAGEMENT_KEYS = new Set([
  'metadata',
  'permissions',
  'componentPermissions',
]);

const COMPONENT_UPDATE_CONTROL_KEYS = new Set([
  'id',
  '_id',
  'componentId',
  'breakpoint',
  'properties',
]);

function isVisualPropertyStyleKey(key: string): boolean {
  return (
    VISUAL_PROPERTY_STYLE_KEYS.has(key) ||
    key.endsWith('Color') ||
    key.endsWith('FontSize') ||
    key.endsWith('FontWeight') ||
    key.endsWith('Radius') ||
    key.endsWith('Opacity')
  );
}

function isStyleMutationKey(key: string): boolean {
  return STYLE_KEYS.has(key) || isVisualPropertyStyleKey(key);
}

function getIdString(id: unknown): string | undefined {
  if (!id) {
    return undefined;
  }

  if (typeof id === 'string') {
    return id;
  }

  if (isRecord(id)) {
    const record = id;
    if (record._id) return String(record._id);
    if (record.$oid) return String(record.$oid);
    if (record.id) return String(record.id);
    if (typeof record.toString === 'function') {
      const stringValue = record.toString();
      if (stringValue !== '[object Object]') {
        return stringValue;
      }
    }
  }

  return undefined;
}

function hasVerifiedPlatformAdminEntitlement(
  user: User | null | undefined,
): boolean {
  return hasAuthoritativePlatformAdminEntitlement(user);
}

function isProjectEditor(project: Project | null | undefined, user: User | null | undefined): boolean {
  const userIds = getViewerPermissionIdStrings(user?.id, user?._id);

  if (!project || userIds.size === 0) {
    return false;
  }

  if (hasVerifiedPlatformAdminEntitlement(user)) {
    return true;
  }

  const ownerId = getIdString(project.ownerId) || getIdString(project.owner_id);
  if (ownerId && userIds.has(ownerId)) {
    return true;
  }

  const membership = project.members?.find((member) =>
    userIds.has(getIdString(member.userId) ?? '')
  );
  return Boolean(
    membership &&
      [ProjectRole.OWNER, ProjectRole.ADMIN, ProjectRole.EDITOR].includes(membership.role)
  );
}

function isProjectOwnerOrAdmin(project: Project | null | undefined, user: User | null | undefined): boolean {
  const userIds = getViewerPermissionIdStrings(user?.id, user?._id);

  if (!project || userIds.size === 0) {
    return false;
  }

  if (hasVerifiedPlatformAdminEntitlement(user)) {
    return true;
  }

  const ownerId = getIdString(project.ownerId) || getIdString(project.owner_id);
  if (ownerId && userIds.has(ownerId)) {
    return true;
  }

  const membership = project.members?.find((member) =>
    userIds.has(getIdString(member.userId) ?? '')
  );
  return Boolean(
    membership &&
      [ProjectRole.OWNER, ProjectRole.ADMIN].includes(membership.role)
  );
}

export function getBlockedAICommandMutationMessage(category: AICommandMutationCategory): string {
  return BLOCKED_MESSAGES[category];
}

export function getAICommandMutationCategoryLabel(category: AICommandMutationCategory): string {
  return CATEGORY_LABELS[category];
}

export function getBlockedAICommandPermissionMessage(
  result: Pick<AICommandPermissionResult, 'blockedCategory'> | null | undefined,
  fallbackCategory: AICommandMutationCategory = 'content',
): string {
  return getBlockedAICommandMutationMessage(result?.blockedCategory || fallbackCategory);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function getComponentPermissionRecord(
  component: Pick<Component, 'metadata'> & { permissions?: unknown },
): Record<string, unknown> | undefined {
  const directPermissions = component.permissions;
  if (isRecord(directPermissions)) {
    return directPermissions;
  }

  const metadata = component.metadata;
  if (!isRecord(metadata)) {
    return undefined;
  }

  const componentPermissions = metadata.componentPermissions;
  if (isRecord(componentPermissions)) {
    return componentPermissions;
  }

  const metadataPermissions = metadata.permissions;
  return isRecord(metadataPermissions) ? metadataPermissions : undefined;
}

function permissionAllowed(rule: unknown): boolean | undefined {
  if (typeof rule === 'boolean') {
    return rule;
  }

  if (isRecord(rule)) {
    return typeof rule.allowed === 'boolean' ? rule.allowed : undefined;
  }

  return undefined;
}

function getComponentPermissionKeys(category: AICommandMutationCategory): string[] {
  switch (category) {
    case 'style':
      return [ComponentPermissionType.STYLE, 'style'];
    case 'layout':
      return [
        ComponentPermissionType.EDIT_LAYOUT,
        ComponentPermissionType.RESIZE,
        ComponentPermissionType.MOVE,
        'layout',
      ];
    case 'content':
      return [ComponentPermissionType.EDIT_CONTENT, 'content'];
    case 'insertion':
      return [ComponentPermissionType.CREATE, 'create'];
    case 'delete':
      return [ComponentPermissionType.DELETE, 'delete'];
    case 'visibility':
      return [ComponentPermissionType.UPDATE, 'visibility'];
    case 'canvas':
      return [];
  }
}

function getComponentBlockedCategory(
  categories: AICommandMutationCategory[],
  components: Array<(Pick<Component, 'metadata'> & { permissions?: unknown }) | null | undefined> | undefined,
): AICommandMutationCategory | undefined {
  if (!components?.length) {
    return undefined;
  }

  for (const category of categories) {
    const permissionKeys = getComponentPermissionKeys(category);
    if (permissionKeys.length === 0) {
      continue;
    }

    const blocked = components.some((component) => {
      if (!component) {
        return false;
      }

      const permissionRecord = getComponentPermissionRecord(component);
      return permissionKeys.some((key) => permissionAllowed(permissionRecord?.[key]) === false);
    });

    if (blocked) {
      return category;
    }
  }

  return undefined;
}

function getComponentScopedCategory(
  categories: AICommandMutationCategory[],
): AICommandMutationCategory | undefined {
  return categories.find((category) => getComponentPermissionKeys(category).length > 0);
}

function getTargetComponentsForPermission(
  request: AICommandPermissionRequest,
  options: AICommandPermissionGuardOptions,
): {
  components: Array<AICommandPermissionTarget | null | undefined> | undefined;
  unresolvedTargetIds: string[];
} {
  const targetIds = [...new Set(request.targetComponentIds || [])];

  if (request.targetComponents?.length) {
    const resolvedTargetIds = new Set(
      request.targetComponents
        .map((component) => component?.id)
        .filter((id): id is string => typeof id === 'string' && id.length > 0),
    );
    const missingTargetIds = targetIds.filter((id) => !resolvedTargetIds.has(id));
    if (missingTargetIds.length === 0) {
      return {
        components: request.targetComponents,
        unresolvedTargetIds: [],
      };
    }

    const resolvedMissingComponents: Array<AICommandPermissionTarget | null | undefined> = [];
    const unresolvedTargetIds: string[] = [];
    for (const id of missingTargetIds) {
      const resolved = options.resolveComponentById?.(id);
      if (resolved) {
        resolvedMissingComponents.push(resolved);
      } else {
        unresolvedTargetIds.push(id);
      }
    }

    return {
      components: [...request.targetComponents, ...resolvedMissingComponents],
      unresolvedTargetIds,
    };
  }

  if (targetIds.length === 0) {
    return {
      components: request.targetComponents,
      unresolvedTargetIds: [],
    };
  }

  if (!options.resolveComponentById) {
    return {
      components: [],
      unresolvedTargetIds: targetIds,
    };
  }

  const unresolvedTargetIds: string[] = [];
  const components = targetIds.map((id) => {
    const resolved = options.resolveComponentById?.(id);
    if (!resolved) {
      unresolvedTargetIds.push(id);
    }
    return resolved;
  });

  return {
    components,
    unresolvedTargetIds,
  };
}

export function createAICommandPermissionGuard(
  project: Project | null | undefined,
  user: User | null | undefined,
  options: AICommandPermissionGuardOptions = {},
): AICommandPermissionGuard {
  return (request) => {
    const categories = [...new Set(request.categories)];
    if (categories.length === 0) {
      return { allowed: true };
    }

    const projectContext = request.project ?? project;
    const canEdit = isProjectEditor(projectContext, user);
    const canDelete = isProjectOwnerOrAdmin(projectContext, user);

    const projectBlockedCategory = categories.find((category) =>
      category === 'delete' ? !canDelete : !canEdit
    );
    const { components: targetComponents, unresolvedTargetIds } = getTargetComponentsForPermission(
      request,
      options,
    );
    const componentBlockedCategory = getComponentBlockedCategory(
      categories,
      targetComponents,
    );
    const unresolvedComponentBlockedCategory = unresolvedTargetIds.length > 0
      ? getComponentScopedCategory(categories)
      : undefined;
    const blockedCategory =
      projectBlockedCategory ||
      componentBlockedCategory ||
      unresolvedComponentBlockedCategory;

    if (!blockedCategory) {
      return { allowed: true };
    }

    return {
      allowed: false,
      blockedCategory,
      message: getBlockedAICommandMutationMessage(blockedCategory),
    };
  };
}

export function createCurrentUserAICommandPermissionGuard(
  project: Project | null | undefined,
): AICommandPermissionGuard {
  return createAICommandPermissionGuard(project, useAuthStore.getState().user);
}

export function getMutationCategoriesForChanges(
  changes: Record<string, unknown> | undefined,
  options: { isCanvasMutation?: boolean } = {},
): AICommandMutationCategory[] {
  if (!changes) {
    return [];
  }

  const categories = new Set<AICommandMutationCategory>();

  if (options.isCanvasMutation) {
    for (const key of ['background', 'grid', 'layout', 'theme', 'responsive']) {
      if (changes[key] !== undefined) {
        categories.add('canvas');
      }
    }
  }

  let hasUnclassifiedTopLevelMutationKey = false;
  for (const key of Object.keys(changes)) {
    const categoryCountBeforeKey = categories.size;
    if (PERMISSION_MANAGEMENT_KEYS.has(key)) {
      categories.add('delete');
    }
    if (isStyleMutationKey(key) || key === 'style' || key === 'styleWriteTarget') {
      categories.add('style');
    }
    if (LAYOUT_KEYS.has(key)) {
      categories.add('layout');
    }
    if (CONTENT_KEYS.has(key) && key !== 'properties') {
      categories.add('content');
    }
    if (key === 'locked') {
      categories.add('content');
    }
    if (key === 'hidden' || key === 'visible') {
      categories.add('visibility');
    }
    if (
      categories.size === categoryCountBeforeKey &&
      !COMPONENT_UPDATE_CONTROL_KEYS.has(key)
    ) {
      hasUnclassifiedTopLevelMutationKey = true;
    }
  }

  const style = changes.style;
  if (isRecord(style)) {
    const styleRecord = style;
    if (styleRecord.display !== undefined) {
      categories.add('visibility');
    }
    if (
      styleRecord.gap !== undefined ||
      styleRecord.flexDirection !== undefined ||
      styleRecord.justifyContent !== undefined ||
      styleRecord.alignItems !== undefined ||
      styleRecord.position !== undefined ||
      styleRecord.top !== undefined ||
      styleRecord.left !== undefined ||
      styleRecord.right !== undefined ||
      styleRecord.bottom !== undefined ||
      styleRecord.zIndex !== undefined
    ) {
      categories.add('layout');
    }
  }

  const properties = changes.properties;
  if (isRecord(properties)) {
    const propertyRecord = properties;
    let hasContentProperty = false;

    for (const key of Object.keys(propertyRecord)) {
      if (isStyleMutationKey(key) || key === 'styleWriteTarget') {
        categories.add('style');
        if (LAYOUT_KEYS.has(key)) {
          categories.add('layout');
        }
        if (key === 'display') {
          categories.add('visibility');
        }
        continue;
      }

      if (LAYOUT_KEYS.has(key)) {
        categories.add('layout');
        continue;
      }

      if (key === 'hidden' || key === 'visible' || key === 'display') {
        categories.add('visibility');
        continue;
      }

      hasContentProperty = true;
    }

    if (propertyRecord.hidden !== undefined || propertyRecord.visible !== undefined) {
      categories.add('visibility');
    }
    if (
      propertyRecord.flexDirection !== undefined ||
      propertyRecord.gap !== undefined ||
      propertyRecord.layout !== undefined
    ) {
      categories.add('layout');
    }
    if (hasContentProperty) {
      categories.add('content');
    }
  } else if (changes.properties !== undefined) {
    categories.add('content');
  }

  if (changes.hidden !== undefined || changes.visible !== undefined) {
    categories.add('visibility');
  }
  if (hasUnclassifiedTopLevelMutationKey) {
    categories.add('content');
  }

  return [...categories];
}

export function getMutationCategoriesForBatchComponentUpdate(
  componentUpdate: Record<string, unknown>,
): AICommandMutationCategory[] {
  const categories = getMutationCategoriesForChanges(componentUpdate);
  return categories.length > 0 ? categories : ['content'];
}

export function getMutationCategoriesForCanvasAction(action: CanvasAction): AICommandMutationCategory[] {
  switch (action.type) {
    case 'add_component':
      return ['insertion'];
    case 'delete_component':
      return ['delete'];
    case 'preflight_pm_write_target':
      return ['content'];
    case 'move_component':
    case 'reorder_children':
      return ['layout'];
    case 'add_page':
    case 'replace_page':
      return ['canvas', 'insertion'];
    case 'update_page':
    case 'switch_page':
    case 'create_branch':
      return ['canvas'];
    case 'link_pages':
      return ['canvas', 'insertion'];
    case 'batch_update': {
      const categories = new Set<AICommandMutationCategory>();
      const components = action.payload?.components;
      if (Array.isArray(components)) {
        for (const componentUpdate of components) {
          for (const category of getMutationCategoriesForBatchComponentUpdate(componentUpdate)) {
            categories.add(category);
          }
        }
      }
      return categories.size > 0 ? [...categories] : ['content'];
    }
    case 'update_component':
      return isRecord(action.payload?.changes)
        ? getMutationCategoriesForBatchComponentUpdate(action.payload.changes)
        : ['content'];
    default:
      // Unknown actions are not intentionally read-only, so require the most
      // restrictive existing mutation category instead of treating them as free.
      return ['delete'];
  }
}
